/**
 * Handler class for addon on products
 */

class AddonHandler {
  constructor(currentAddOnProduct, allBlockProducts, isMultiSelection) {
    this.addOnProduct = currentAddOnProduct;
    this.allBlockProducts = allBlockProducts;
    this.isMultiSelection = isMultiSelection;

    this.setAddonSelectors();
    this.setInitialPrices();
    this.checkAddonIsUpsellBlock();
    this.setClickOnUpsellBlock();

    if (!this.qtyInput) return;

    this.setChangeListener();
    this.setIncreaseButtonListener();
    this.setDecreaseButtonListener();
  }

  /**
   * Sets elements used within addon
   */
  setAddonSelectors() {
    const container = this.addOnProduct.closest('[data-add-on-item]');

    this.qtyInput = container?.querySelector('[data-add-on-qty]');
    this.btnPlus = container?.querySelector('[data-qty-increase]');
    this.btnMinus = container?.querySelector('[data-qty-decrease]');
    this.addOnPrice = container?.querySelector('[data-add-on-price]');
    this.addOnComparePrice = container?.querySelector('[data-add-on-compare-price]');
  }

  /**
   * Sets initial pricing values with no addon added
   */
  setInitialPrices() {
    this.initialPrice = parseFloat(this.addOnPrice?.innerText.replace(',', '.').replace(/[^0-9.]/g, ''));
    this.initialComparePrice = parseFloat(this.addOnComparePrice?.innerText.replace(',', '.').replace(/[^0-9.]/g, ''));
    this.cartPrice = document.querySelector('.product-form .custom-price-section');
    this.initialCartPrice = parseFloat(this.cartPrice?.innerText.replace(',', '.').replace(/[^0-9.]/g, ''));
  }

  /**
   * Set flag if addon is part of upsell items block
   */
  checkAddonIsUpsellBlock() {
    this.isUpsellBlock = this.addOnProduct.hasAttribute('upsell-item-block');
  }

  /**
   * Set click event on item (for non explicit input field)
   */
  setClickOnUpsellBlock() {
    if (this.isUpsellBlock) {
      this.addOnProduct.parentElement.addEventListener('click', () => {
        this.deselectItems();

        this.addOnProduct.checked = !this.addOnProduct.checked;

        this.setUpdatedPrices();
        this.setTotalCartPrice();
        this.setTotalComparePrice();
      });
    }
  }

  /**
   * If block is not multi-choice, deselect items
   */
  deselectItems() {
    if (!this.isMultiSelection) {
      this.allBlockProducts.forEach((product) => {
        if (product !== this.addOnProduct && product.checked) {
          product?.parentNode?.click();
        }
      });
    }
  }

  /**
   * Set on change listener on add on product
   */
  setChangeListener() {
    this.addOnProduct.addEventListener('change', () => {
      this.qtyInput.disabled = !this.addOnProduct.checked;

      this.setQuantitySelectorValue();

      if (this.addOnProduct.checked) {
        this.setUpdatedPrices();
        this.setTotalPrice();
        this.setTotalCartPrice();
        this.setTotalComparePrice();
        this.setComparePrice();
      } else {
        this.setTotalCartPrice();
        this.setTotalComparePrice();
      }
    });
  }

  /**
   * Sets default quantity input field value
   */
  setQuantitySelectorValue() {
    if (this.addOnProduct.checked && (!this.qtyInput.value || parseInt(this.qtyInput.value, 10) < 1))
      this.qtyInput.value = 1;
  }

  /**
   * Listen click on increase button
   */
  setIncreaseButtonListener() {
    this.btnPlus?.addEventListener('click', (event) => {
      event.stopPropagation();

      this.increaseQuantitySelectorValue();
      this.handleAddingOnCartPrice();

      if (this.qtyInput.disabled) {
        this.addOnProduct.checked = true;
        this.qtyInput.disabled = false;
      }

      this.setUpdatedPrices();
      this.setTotalPrice();
      this.setComparePrice();
    });
  }

  /**
   * Adds on input field value - used for increase button click
   */
  increaseQuantitySelectorValue() {
    this.qtyInput.value = parseInt(this.qtyInput.value || '1', 10) + 1;
  }

  /**
   * Handling adding increased addon price on cart total
   *
   * Takes into account cases when:
   *  - item is not selected and quantity is 1 - adds to cart price with updated quantity
   *  - item is selected - add to cart price with single item value
   *  - ignores the rest of the cases
   */
  handleAddingOnCartPrice() {
    if (!this.addOnProduct.checked && this.qtyInput.value == 2) {
      this.deselectItems();

      miraGlobal?.customVariables?.addToCartPrice.add(this.initialPrice * this.qtyInput.value * 100);

      return;
    }

    if (this.addOnProduct.checked) {
      miraGlobal?.customVariables?.addToCartPrice.add(this.initialPrice * 100);

      return;
    }
  }

  /**
   * Listen click on decrease button
   */
  setDecreaseButtonListener() {
    this.btnMinus?.addEventListener('click', (event) => {
      event.stopPropagation();

      if (this.qtyInput.disabled) return;

      const current = parseInt(this.qtyInput.value || '1', 10);

      if (current == 1) {
        this.subtractAddonInitialPrice();

        this.addOnProduct.checked = false;
        this.qtyInput.disabled = true;
      } else {
        this.qtyInput.value = current - 1;

        this.setUpdatedPrices();
        this.setTotalPrice();
        this.setComparePrice();
        this.subtractAddonInitialPrice();
      }
    });
  }

  /**
   * Subtract addon single item value from cart value and updates ATC button pricing
   */
  subtractAddonInitialPrice() {
    if (this.addOnProduct.checked) {
      miraGlobal?.customVariables?.addToCartPrice.remove(this.initialPrice * 100);
    }
  }

  /**
   * Modifiers for quantity updates
   */
  setUpdatedPrices() {
    this.updatedPrice = this.initialPrice * this.qtyInput.value;
    this.updatedComparePrice = this.initialComparePrice * this.qtyInput.value;
  }

  /**
   * Setting addon price - used when addon product quantity changes
   */
  setTotalPrice() {
    this.addOnPrice.innerText = miraGlobal?.formatters?.formatMoney(this.updatedPrice);
  }

  /**
   * Setting cart price - used when addon product quantity changes
   *
   * Add product total value to cart value if addon selected
   * Subtract product total value from cart value if addon deselected
   */
  setTotalCartPrice() {
    if (this.addOnProduct.checked) {
      miraGlobal?.customVariables?.addToCartPrice?.add(this.updatedPrice * 100);

      return;
    }

    miraGlobal?.customVariables?.addToCartPrice?.remove(this.updatedPrice * 100);
  }

  /**
   * Setting cart old price - used when addon product quantity changes
   *
   * Add product total value to cart value if addon selected
   * Subtract product total value from cart value if addon deselected
   */
  setTotalComparePrice() {
    if (isNaN(this.updatedComparePrice)) return;

    if (this.addOnProduct.checked) {
      miraGlobal?.customVariables?.addToCartOldPrice?.add(this.updatedComparePrice * 100);

      return;
    }

    miraGlobal?.customVariables?.addToCartOldPrice?.remove(this.updatedComparePrice * 100);
  }

  /**
   * Setting compare price - used when addon product quantity changes
   */
  setComparePrice() {
    const formattedComparePrice = miraGlobal?.formatters?.formatMoney(this.updatedComparePrice);

    if (this.updatedComparePrice != null && !Number.isNaN(this.updatedComparePrice)) {
      this.addOnComparePrice.innerText = formattedComparePrice;
    }
  }
}
